import { useEffect } from 'react';
import { propertyTypeOptionsByRiskType, residenceUsageOptionsByRiskType } from './farm-helpers';

/**
 * Hook to clear invalid Structure Category and Residence Type/Usage values
 * when risk type changes. Ensures previously selected values that are no longer
 * valid for the new risk type are automatically cleared.
 *
 * @param {Object} options
 * @param {string} options.riskType - Current risk type (FO1, FO2, FO3, etc.)
 * @param {string} options.propertyType - Current propertyType value (optional)
 * @param {string} options.residenceUsage - Current residenceUsage value (optional)
 * @param {Function} options.setFieldValue - Formik setFieldValue function
 * @param {string} options.propertyTypePath - Field path for propertyType (optional, skip if not provided)
 * @param {string} options.residenceUsagePath - Field path for residenceUsage (optional, skip if not provided)
 */
export const useClearInvalidRiskOptions = ({
  riskType,
  propertyType,
  residenceUsage,
  setFieldValue,
  propertyTypePath,
  residenceUsagePath
}) => {
  useEffect(() => {
    if (!riskType) {
      return;
    }

    const validPropertyTypes = propertyTypeOptionsByRiskType[riskType];
    const validResidenceUsages = residenceUsageOptionsByRiskType[riskType];

    // Clear propertyType if path provided and value is no longer valid
    if (
      propertyTypePath &&
      propertyType &&
      validPropertyTypes &&
      !validPropertyTypes.some((opt) => opt.id === propertyType)
    ) {
      setFieldValue(propertyTypePath, undefined);
    }

    // Clear residenceUsage if path provided and value is no longer valid
    if (
      residenceUsagePath &&
      residenceUsage &&
      validResidenceUsages &&
      !validResidenceUsages.some((opt) => opt.id === residenceUsage)
    ) {
      setFieldValue(residenceUsagePath, undefined);
    }
  }, [riskType, propertyType, residenceUsage, setFieldValue, propertyTypePath, residenceUsagePath]);
};
